import { suspend } from "suspend-react";
import { requestCameraPermission } from "./utils/cameraPermission";
import { requestMotionPermission } from "./utils/motionPermission";
import { useStore } from "./store/Store";
import { ACTION_TRIGGERS } from "./ActionTriggers";
import { PermissionsDeniedComponent } from "../../components/PermissionsDeniedComponent";
import { useEffect, useState } from "react";

type PermissionProps = {
  styledPermissionPage: JSX.Element;
  children: React.ReactNode;
  requestAudio?: boolean;
};

/**
 * Asking for permissions
 *
 * When asking for permissions we need a user input for each permission we are asking for. This leaves us with two options
 *
 *  1. Show both permissions with one button click so we only have to show one styled asking for permission screen
 *
 *  2. Show each permission after the other. Then we need two styled pages asking for two different
 *     permissions and more clicks that the user needs to do.
 *
 *  Opting for option 1 for now.
 *
 *
 * @param props {styledPermissionPage: JSX.Element, children: React.ReactNode}
 * @returns Styled permission page or children or error
 */
export const PermissionComponent: React.FC<PermissionProps> = (
  props: PermissionProps
) => {
  const [permissionsDenied, setPermissionsDenied] = useState(false);
  const [renderApp, setRenderApp] = useState(true);

  const askPermissions = useStore((state) => {
    return state.askPermissions;
  });

  const triggerAction = useStore((store) => store.triggerAction);
  const addAction = useStore((store) => store.addAction);

  useEffect(() => {
    addAction({
      target: "App",
      trigger: ACTION_TRIGGERS.ENABLE_POST_PERMISSION_CONTENT,
      cb: (target, event) => {
        setRenderApp(!event);
      },
    });
  });

  if (!askPermissions) {
    return <>{props.styledPermissionPage}</>;
  }

  const { cameraPerm, sensorPerm } = suspend(async () => {
    // TODO: re-order
    const sensorPerm = await requestMotionPermission();
    const cameraPerm = await requestCameraPermission(props.requestAudio);
    // const cameraPermFunction = async () => {
    //     return await cameraPerm;
    // }
    // const _cameraPerm = await cameraPermFunction();
    return { sensorPerm, cameraPerm };
  }, ["cameraPermission"]);

  if (cameraPerm === "granted" && sensorPerm === "granted") {
    triggerAction(ACTION_TRIGGERS.PERMISSION_GRANTED, "permission");

    const PostPermissionComponentFound = (props.children as Array<any>).find(
      ({ type }) => type.name === "PostPermissionComponent"
    );

    if (PostPermissionComponentFound && !renderApp) {
      return <>{PostPermissionComponentFound}</>;
    } else {
      return <>{props.children}</>;
    }
  } else {
    setTimeout(() => {
      setPermissionsDenied(true);
    }, 500);
    return (
      <PermissionsDeniedComponent
        permissionsDenied={permissionsDenied}
      ></PermissionsDeniedComponent>
    );
  }
};
